import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { FiCalendar, FiClock, FiMapPin, FiDollarSign, FiCheckCircle, FiXCircle, FiClock as FiClockIcon, FiImage, FiChevronDown, FiChevronUp } from 'react-icons/fi';
import { formatDateTime, formatDuration } from '../data/carsData';
import { Card, Loading } from '../components/common';
import Navbar from '../components/Navbar';
import { useAuth } from '../context/AuthContext';
import { getBookingsByUser, getBookingById, deleteBookingImage } from '../firebase/bookings';
import BookingImageUpload from '../components/BookingImageUpload';
import BookingImageGallery from '../components/BookingImageGallery';

const BookingHistory = () => {
  const navigate = useNavigate();
  const { user, userProfile } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedBooking, setExpandedBooking] = useState(null);

  useEffect(() => {
    const loadBookings = async () => {
      if (!user) {
        setLoading(false);
        return;
      }
      
      setLoading(true);
      try {
        const result = await getBookingsByUser(user.uid);
        if (result.bookings) {
          // Map bookings to include car info
          const bookingsWithCar = result.bookings.map(booking => ({
            ...booking,
            car: booking.carDetails || null
          }));
          
          setBookings(bookingsWithCar);
        }
      } catch (error) {
        console.error('Error loading bookings:', error);
      } finally {
        setLoading(false);
      }
    };
    
    loadBookings();
  }, [user]);

  const handleImageUploadComplete = async (bookingId) => {
    // Reload the specific booking to get updated images
    try {
      const result = await getBookingById(bookingId);
      if (result.booking) {
        setBookings(prev => prev.map(b => 
          b.id === bookingId ? { ...b, ...result.booking } : b
        ));
      }
    } catch (error) {
      console.error('Error reloading booking:', error);
    }
  };

  const handleImageDelete = async (bookingId, imageId, imageType, imagePath) => {
    try {
      const result = await deleteBookingImage(bookingId, imageId, imageType, imagePath);
      if (result.error) {
        alert(`Failed to delete image: ${result.error}`);
        return;
      }
      
      // Reload booking
      await handleImageUploadComplete(bookingId);
    } catch (error) {
      console.error('Error deleting image:', error);
      alert('Failed to delete image. Please try again.');
    }
  };

  const canUserUpload = (booking) => {
    // User can upload only when status is 'confirmed' and booking is not completed
    return booking.status === 'confirmed';
  };

  const getStatusBadge = (status, dateOrDateTime) => {
    // Show status badge based on booking status
    if (status === 'cancelled') {
      return (
        <span className="px-3 py-1 bg-red-100 text-red-700 rounded-full text-xs font-semibold flex items-center gap-1">
          <FiXCircle className="w-3 h-3" />
          Cancelled
        </span>
      );
    }
    
    if (status === 'pending') {
      return (
        <span className="px-3 py-1 bg-yellow-100 text-yellow-700 rounded-full text-xs font-semibold flex items-center gap-1">
          <FiClockIcon className="w-3 h-3" />
          Pending Approval
        </span>
      );
    }
    
    if (status === 'completed') {
      return (
        <span className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-xs font-semibold flex items-center gap-1">
          <FiCheckCircle className="w-3 h-3" />
          Completed
        </span>
      );
    }
    
    if (status === 'confirmed') {
      if (!dateOrDateTime) {
        return (
          <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-xs font-semibold flex items-center gap-1">
            <FiCheckCircle className="w-3 h-3" />
            Confirmed
          </span>
        );
      }
      
      const bookingDate = new Date(dateOrDateTime);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const bookingDateOnly = new Date(bookingDate);
      bookingDateOnly.setHours(0, 0, 0, 0);
      
      const isPast = bookingDateOnly < today;
      const isToday = bookingDateOnly.getTime() === today.getTime();
      
      if (isPast) {
        return (
          <span className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-xs font-semibold flex items-center gap-1">
            <FiCheckCircle className="w-3 h-3" />
            Completed
          </span>
        );
      }
      
      if (isToday) {
        return (
          <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-semibold flex items-center gap-1">
            <FiClockIcon className="w-3 h-3" />
            Today
          </span>
        );
      }
      
      return (
        <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-xs font-semibold flex items-center gap-1">
          <FiCheckCircle className="w-3 h-3" />
          Upcoming
        </span>
      );
    }
    
    // Default for any other status
    return (
      <span className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-xs font-semibold flex items-center gap-1">
        <FiClockIcon className="w-3 h-3" />
        {status || 'Unknown'}
      </span>
    );
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return <Loading fullScreen text="Loading booking history..." />;
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Card className="p-8 text-center max-w-md">
          <h2 className="text-2xl font-semibold text-gray-900 mb-4">Please Sign In</h2>
          <p className="text-gray-600 mb-6">You need to be signed in to view your booking history.</p>
          <Link to="/login">
            <button className="gradient-primary text-white px-6 py-3 rounded-lg font-medium">
              Sign In
            </button>
          </Link>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6 lg:py-8">
        <div className="mb-6 sm:mb-8">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-1 sm:mb-2">My Bookings</h1>
          <p className="text-sm sm:text-base text-gray-600">View and manage all your car bookings</p>
        </div>

        {bookings.length === 0 ? (
          <Card className="p-12 text-center border-0 shadow-soft">
            <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <FiCalendar className="w-12 h-12 text-gray-400" />
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">No Bookings Yet</h3>
            <p className="text-gray-600 mb-6">You haven't made any bookings yet. Start by booking your first car!</p>
            <Link to="/">
              <button className="gradient-primary text-white px-6 py-3 rounded-lg font-medium">
                Browse Cars
              </button>
            </Link>
          </Card>
        ) : (
          <div className="space-y-4 sm:space-y-6">
            {bookings.map((booking) => (
              <Card key={booking.id} className="p-4 sm:p-6 border-0 shadow-soft hover:shadow-medium transition-shadow">
                <div className="flex flex-col md:flex-row gap-4 sm:gap-6">
                  {/* Car Image */}
                  {(booking.car || booking.carDetails) && (
                    <div className="md:w-48 flex-shrink-0">
                      <img
                        src={booking.carDetails?.image || booking.car?.images?.[0] || booking.car?.image || ''}
                        alt={booking.carDetails?.name || booking.car?.name || 'Car'}
                        className="w-full h-40 sm:h-32 object-contain bg-gray-100 rounded-xl"
                      />
                    </div>
                  )}

                  {/* Booking Details */}
                  <div className="flex-1">
                    <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3 sm:gap-4 mb-3 sm:mb-4">
                      <div className="flex-1">
                        {(booking.car || booking.carDetails) ? (
                          <>
                            <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-1">
                              {booking.carDetails?.name || booking.car?.name}
                            </h3>
                            <p className="text-xs sm:text-sm text-gray-600 mb-2">
                              {booking.carDetails?.brand || booking.car?.brand} • {booking.carDetails?.model || booking.car?.model}
                            </p>
                          </>
                        ) : (
                          <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-1">
                            Car ID: {booking.carId}
                          </h3>
                        )}
                        <div className="mt-2">
                          {getStatusBadge(booking.status, booking.fromDateTime || booking.date)}
                        </div>
                      </div>
                      <div className="text-left md:text-right">
                        <p className="text-xs sm:text-sm text-gray-500 mb-1">Booking ID</p>
                        <p className="font-mono text-xs sm:text-sm font-semibold text-gray-900 break-all">{booking.id}</p>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 mb-3 sm:mb-4">
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 bg-primary-50 rounded-lg flex items-center justify-center flex-shrink-0">
                          <FiCalendar className="w-5 h-5 text-primary-600" />
                        </div>
                        {booking.fromDateTime ? (
                          <div>
                            <p className="text-sm text-gray-500 mb-1">Booking Duration</p>
                            <p className="font-semibold text-gray-900 text-sm">
                              {booking.duration || 'N/A'}
                            </p>
                          </div>
                        ) : booking.date ? (
                          <div>
                            <p className="text-sm text-gray-500 mb-1">Booking Date</p>
                            <p className="font-semibold text-gray-900">{formatDate(booking.date)}</p>
                          </div>
                        ) : null}
                      </div>

                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center flex-shrink-0">
                          <FiClock className="w-5 h-5 text-blue-600" />
                        </div>
                        <div>
                          <p className="text-sm text-gray-500 mb-1">Booking Period</p>
                          <p className="font-semibold text-gray-900 text-sm">
                            {booking.fromDateTime ? (
                              <>
                                {formatDateTime(booking.fromDateTime)}
                                <br />
                                <span className="text-gray-600">to</span>
                                <br />
                                {formatDateTime(booking.toDateTime)}
                              </>
                            ) : (
                              'Details not available'
                            )}
                          </p>
                        </div>
                      </div>
                    </div>

                    {booking.car && (
                      <div className="pt-4 border-t border-gray-200">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2 text-gray-600">
                            <FiDollarSign className="w-4 h-4" />
                            <span className="text-sm">Total Amount</span>
                          </div>
                          <span className="text-xl font-bold text-primary-600">
                            ₹{(booking.totalAmount || booking.subtotal || 0).toLocaleString()}
                          </span>
                        </div>
                      </div>
                    )}

                    {/* Car Images Section */}
                    {(canUserUpload(booking) || booking.images?.beforeRide?.length > 0 || booking.images?.afterRide?.length > 0) && (
                      <div className="pt-4 border-t border-gray-200 mt-4">
                        <div className="flex items-center justify-between mb-3">
                          <h4 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                            <FiImage size={16} />
                            Car Images
                          </h4>
                          <button
                            onClick={() => setExpandedBooking(expandedBooking === booking.id ? null : booking.id)}
                            className="text-primary-600 hover:text-primary-700 p-1 rounded transition-colors"
                            title={expandedBooking === booking.id ? 'Hide Images' : 'View Images'}
                          >
                            {expandedBooking === booking.id ? (
                              <FiChevronUp size={18} />
                            ) : (
                              <FiChevronDown size={18} />
                            )}
                          </button>
                        </div>

                        {/* Show images only when expanded */}
                        {expandedBooking === booking.id && (
                          <>
                            {/* Before Ride Images */}
                            {booking.images?.beforeRide && booking.images.beforeRide.length > 0 && (
                              <div className="mb-4">
                                <BookingImageGallery
                                  images={booking.images.beforeRide}
                                  imageType="before-ride"
                                  currentUserId={user?.uid}
                                  onDelete={(imageId, imageType, imagePath) => handleImageDelete(booking.id, imageId, imageType, imagePath)}
                                  canDelete={canUserUpload(booking)}
                                />
                              </div>
                            )}

                            {/* After Ride Images (Manager uploaded) */}
                            {booking.images?.afterRide && booking.images.afterRide.length > 0 && (
                              <div className="mb-4">
                                <BookingImageGallery
                                  images={booking.images.afterRide}
                                  imageType="after-ride"
                                  currentUserId={user?.uid}
                                  onDelete={null}
                                  canDelete={false}
                                />
                              </div>
                            )}

                            {/* Upload Section (only for confirmed bookings) */}
                            {canUserUpload(booking) && (
                              <div className="space-y-6 mt-4">
                                <div className="p-4 bg-gray-50 rounded-lg">
                                  <h5 className="text-sm font-medium text-gray-900 mb-3">Before Ride Images</h5>
                                  <p className="text-xs text-gray-600 mb-3">
                                    Upload images of the car before your ride starts to document any existing damages or conditions.
                                  </p>
                                  <BookingImageUpload
                                    bookingId={booking.id}
                                    imageType="before-ride"
                                    currentImages={booking.images?.beforeRide || []}
                                    currentUserId={user?.uid}
                                    currentUserName={userProfile?.displayName || user?.displayName || user?.email?.split('@')[0] || 'User'}
                                    onUploadComplete={() => handleImageUploadComplete(booking.id)}
                                    onDelete={(imageId, imageType, imagePath) => handleImageDelete(booking.id, imageId, imageType, imagePath)}
                                    maxImages={15}
                                  />
                                </div>
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default BookingHistory;

